/**
 * PCM Zopim/Zendesk Chat API Helpers
 *
 * Global wrappers around the Zopim/Zendesk `$zopim` widget API so templates
 * and other scripts can tag/identify a chat visitor or open the widget
 * without needing to know whether the widget has finished loading yet -
 * every call is deferred via `$zopim(callback)`, which Zopim itself queues
 * until the widget is ready.
 *
 * Also the one place the widget's callbacks are registered. setOnConnected,
 * setOnStatus, setOnChatStart and window.onShow/onHide each hold ONE
 * handler - registering again replaces the last one - so they're set here
 * once and re-broadcast on document as pcm:zopimReady, pcm:zopimConnected,
 * pcm:zopimStatus ({status}), pcm:zopimChatStart, pcm:zopimWindowShow and
 * pcm:zopimWindowHide. Listen to those instead of calling the setters.
 *
 * And whether chat is available: `window.pcmChatAvailable()`, mirrored on
 * <html> as .pcm-chat-available (pcm:chatAvailability when it changes).
 * Invitations follow the legacy page's schedule: configured chat hours, or
 * all day on the verified gated LPs. Zendesk's status updates the displayed
 * availability label without removing the invitation. An explicit setting
 * disabling chat prompts is still respected.
 *
 * `window.pcmShowZopimChat` is consumed by faux-chat-widget.js.
 */
(function(window, document) {
  'use strict';

  const root = document.documentElement;
  const hub = { bound: false, ready: false, readyAt: 0, connected: false, status: null };
  let lastAvailable = null;

  function defineGlobal(name, callback) {
    if (typeof window[name] !== 'function') {
      window[name] = callback;
    }
  }

  function emit(name, detail) {
    let event;

    try {
      event = new CustomEvent(name, { detail: detail || {} });
    } catch (e) {
      event = document.createEvent('CustomEvent');
      event.initCustomEvent(name, false, false, detail || {});
    }

    document.dispatchEvent(event);
  }

  function once(name, callback) {
    function handler(event) {
      document.removeEventListener(name, handler);
      callback(event);
    }

    document.addEventListener(name, handler);
  }

  function chatAvailable() {
    if (!window.pcmChatHours) {
      return false;
    }

    return !!window.pcmChatPromptsAlways
      || (typeof window.pcmChatClockOpen === 'function' && window.pcmChatClockOpen());
  }

  function updatePromptStatus() {
    document.querySelectorAll('[data-pcm-chat-status]').forEach(function(label) {
      // Until Zendesk reports, don't present an automated prompt as proof
      // that an agent is online or has joined this conversation.
      const status = hub.status || 'unknown';
      if (label.getAttribute('data-pcm-reported-status') === status) {
        return;
      }
      label.setAttribute('data-pcm-reported-status', status);
      label.textContent = status === 'online' ? 'online now'
        : status === 'away' ? 'Chat available'
        : status === 'offline' ? 'Leave a message' : 'Send a message';
      if (status === 'online' || status === 'away') {
        label.insertBefore(document.createElement('span'), label.firstChild);
      }
    });
  }

  function updateAvailability() {
    const available = chatAvailable();

    root.classList.toggle('pcm-chat-available', available);
    updatePromptStatus();

    if (available !== lastAvailable) {
      lastAvailable = available;
      emit('pcm:chatAvailability', { available: available });
    }
  }

  let bindPoll = null;

  function bindZopim() {
    if (hub.bound) {
      return true;
    }

    // The script inc/enqueues.php loads is only Zendesk's loader: $zopim
    // turns up a moment after its onload, so keep looking for a minute.
    if (typeof window.$zopim !== 'function' || !window.$zopim.livechat) {
      if (!bindPoll) {
        let attempts = 0;

        bindPoll = window.setInterval(function() {
          if (++attempts > 600 || bindZopim()) {
            window.clearInterval(bindPoll);
            bindPoll = null;
          }
        }, 100);
      }

      return false;
    }

    hub.bound = true;

    window.$zopim(function() {
      const livechat = window.$zopim.livechat;

      if (!livechat) {
        return;
      }

      if (typeof livechat.setOnConnected === 'function') {
        livechat.setOnConnected(function() {
          hub.connected = true;
          emit('pcm:zopimConnected');
        });
      }

      if (typeof livechat.setOnStatus === 'function') {
        livechat.setOnStatus(function(status) {
          hub.status = status;
          emit('pcm:zopimStatus', { status: status });
          updateAvailability();
        });
      }

      if (typeof livechat.setOnChatStart === 'function') {
        livechat.setOnChatStart(function() {
          root.classList.add('pcm-chat-started');
          emit('pcm:zopimChatStart');
        });
      }

      if (livechat.window) {
        if (typeof livechat.window.onShow === 'function') {
          livechat.window.onShow(function() {
            root.classList.add('pcm-zopim-window-open');
            emit('pcm:zopimWindowShow');
          });
        }

        if (typeof livechat.window.onHide === 'function') {
          livechat.window.onHide(function() {
            root.classList.remove('pcm-zopim-window-open');
            emit('pcm:zopimWindowHide');
          });
        }
      }

      hub.ready = true;
      hub.readyAt = Date.now();
      emit('pcm:zopimReady');
    });

    return true;
  }

  function whenReady(callback) {
    if (hub.ready) {
      callback();
      return;
    }

    once('pcm:zopimReady', callback);
  }

  // say() needs a connected widget. A widget that connected before its
  // callbacks were registered never reports it, so five seconds after it's
  // ready counts as connected.
  function whenConnected(callback) {
    const waited = Date.now() - hub.readyAt;

    if (hub.connected || waited >= 5000) {
      callback();
      return;
    }

    let done = false;

    function run() {
      if (done) {
        return;
      }

      done = true;
      document.removeEventListener('pcm:zopimConnected', run);
      callback();
    }

    document.addEventListener('pcm:zopimConnected', run);
    window.setTimeout(run, 5000 - waited);
  }

  window.pcmZopimBind = bindZopim;
  window.pcmChatAvailable = chatAvailable;

  defineGlobal('zopimSetChatMessageTag', function(tag) {
    if (typeof window.$zopim !== 'function') {
      return;
    }

    window.$zopim(function() {
      if (window.$zopim.livechat && window.$zopim.livechat.addTags && tag) {
        window.$zopim.livechat.addTags(tag);
      }
    });
  });

  defineGlobal('zopimSetUser', function(email, name, phone) {
    if (typeof window.$zopim !== 'function') {
      return;
    }

    window.$zopim(function() {
      if (!window.$zopim.livechat) {
        return;
      }

      if (name && window.$zopim.livechat.setName) {
        window.$zopim.livechat.setName(name);
      }

      if (email && window.$zopim.livechat.setEmail) {
        window.$zopim.livechat.setEmail(email);
      }

      if (phone && window.$zopim.livechat.setPhone) {
        window.$zopim.livechat.setPhone(phone);
      }
    });
  });

  defineGlobal('zopimSetChatGroup', function(group) {
    if (typeof window.$zopim !== 'function') {
      return;
    }

    window.$zopim(function() {
      if (window.$zopim.livechat && window.$zopim.livechat.departments && group) {
        window.$zopim.livechat.departments.setVisitorDepartment(group);
      }
    });
  });

  // Pure no-ops, kept for the same reason as legacy-global-stubs.js - old
  // content may still call these by name.
  defineGlobal('zopimCallbacks', function() {
    return false;
  });

  defineGlobal('zopimEndChatLoop', function() {
    return false;
  });

  // Opens the chat window and, given a message, sends it as the visitor.
  // When the widget isn't loaded - cookie consent still pending or declined -
  // the visitor asking to chat loads it (pcmLoadZendeskChatNow in
  // inc/enqueues.php) and the message goes once it connects. False only when
  // chat can't be loaded at all: logged-in staff, or ?disablechat=true.
  defineGlobal('pcmShowZopimChat', function(message) {
    if (typeof window.$zopim === 'function') {
      bindZopim();
    } else if (typeof window.pcmLoadZendeskChatNow !== 'function' || !window.pcmLoadZendeskChatNow()) {
      return false;
    }

    // Lets the window back above an open popup or modal, which otherwise
    // keep it beneath them (sass/partials/_fauxchat.scss): their chat steps
    // hand off to it and stay open behind it.
    document.documentElement.classList.add('pcm-chat-requested');

    whenReady(function() {
      whenConnected(function() {
        const livechat = window.$zopim && window.$zopim.livechat;

        if (!livechat) {
          return;
        }

        if (livechat.window && livechat.window.show) {
          livechat.window.show();
        }

        if (message && livechat.say) {
          livechat.say(message);
        }
      });
    });

    return true;
  });

  // Normally the widget's loader calls pcmZopimBind() once it's in (inc/enqueues.php).
  if (typeof window.$zopim === 'function') {
    bindZopim();
  }
  updateAvailability();
  // The clock answer goes stale on a page left open across opening or
  // closing time.
  window.setInterval(updateAvailability, 60000);
})(window, document);
